/* eslint-disable no-magic-numbers -- 3D navigation math and rendering tolerances */
/* eslint-disable max-lines -- moved verbatim from web3d; splitting it is tracked by MERCATOR-322 */
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Frustum,
  Matrix4,
  Mesh,
  Object3D,
  Plane,
  Scene,
  Vector3,
} from '@technology/web3d';
import type {
  ContourGroupResult,
  ContourView,
} from './cross-section-contour-computer.types';

const _va = new Vector3();
const _invMatrix = new Matrix4();
const _localPlane = new Plane();
const _localFrustumBox = new Box3();
const _projScreenMatrix = new Matrix4();
const _frustum = new Frustum();

type IndexArray = Uint16Array | Uint32Array;

interface CachedGeometry {
  positions: Float32Array;
  index: IndexArray | null;
  indexCount: number;
  bbMin: [number, number, number];
  bbMax: [number, number, number];
  matrixWorld: Matrix4;
  matrixKey: string;
  isBimMesh: boolean;
}

/**
 * Computes the intersection of a clipping plane with all visible BIM mesh
 * geometry. Works entirely in each mesh's local coordinate space to avoid
 * float32 precision issues with large world coordinates.
 */
export class CrossSectionContourComputer {
  private readonly _geometryCache = new Map<string, CachedGeometry>();
  private readonly _geometryCacheWeak = new WeakSet<BufferGeometry>();

  private _segmentBuffer = new Float32Array(4096 * 6);
  private _segmentCount = 0;

  public clearCache(): void {
    this._geometryCache.clear();
  }

  public cacheSceneGeometry(scene: Scene): void {
    scene.updateMatrixWorld(false);

    const activeGeometryKeys = new Set<string>();

    scene.traverse((obj: Object3D) => {
      if (!(obj instanceof Mesh)) return;
      const geometry = obj.geometry as BufferGeometry;
      if (!geometry) return;
      activeGeometryKeys.add(geometry.uuid);
      this._getOrCacheGeometry(geometry, obj as Mesh);
    });

    for (const key of this._geometryCache.keys()) {
      if (!activeGeometryKeys.has(key)) {
        this._geometryCache.delete(key);
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 25 > 15 allowed; refactor later
  public compute(
    intersectionPlane: Plane,
    _clippingPlanes: Plane[],
    view: ContourView,
    _modelRoots?: Object3D[]
  ): ContourGroupResult[] {
    view.scene?.updateMatrixWorld(true);

    const camera = view.camera;
    let hasViewBounds = false;
    if (camera) {
      camera.updateMatrixWorld(true);
      _projScreenMatrix.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
      );
      _frustum.setFromProjectionMatrix(_projScreenMatrix);
      hasViewBounds = true;
    }

    interface CacheGroup {
      mw: Matrix4;
      entries: CachedGeometry[];
    }
    const groupMap = new Map<string, CacheGroup>();

    for (const cached of this._geometryCache.values()) {
      if (!cached.isBimMesh) continue;
      if (!cached.matrixKey) continue;

      let group = groupMap.get(cached.matrixKey);
      if (!group) {
        group = { mw: cached.matrixWorld.clone(), entries: [] };
        groupMap.set(cached.matrixKey, group);
      }
      group.entries.push(cached);
    }

    const results: ContourGroupResult[] = [];
    const _bb = new Box3();

    for (const group of groupMap.values()) {
      this._segmentCount = 0;

      _invMatrix.copy(group.mw).invert();
      _localPlane.copy(intersectionPlane).applyMatrix4(_invMatrix);

      let useLocalFrustum = false;
      if (hasViewBounds) {
        this._computeLocalFrustumBox(group.mw);
        useLocalFrustum = true;
      }

      for (const cached of group.entries) {
        _bb.min.set(cached.bbMin[0], cached.bbMin[1], cached.bbMin[2]);
        _bb.max.set(cached.bbMax[0], cached.bbMax[1], cached.bbMax[2]);

        if (!_localPlane.intersectsBox(_bb)) continue;
        if (useLocalFrustum && !_localFrustumBox.intersectsBox(_bb)) continue;

        const total = cached.index
          ? cached.indexCount
          : cached.positions.length / 3;
        this._intersectRange(
          cached.positions,
          cached.index,
          0,
          total,
          useLocalFrustum
        );
      }

      if (this._segmentCount > 0) {
        results.push({
          segmentCount: this._segmentCount,
          segments: new Float32Array(
            this._segmentBuffer.subarray(0, this._segmentCount * 6)
          ),
          matrixWorld: group.mw.clone(),
        });
      }
    }

    return results;
  }

  /**
   * Compute a local-space AABB that encloses the camera frustum.
   * We transform the 8 frustum corners from NDC to world, then to local space.
   */
  private _computeLocalFrustumBox(_modelMatrixWorld: Matrix4): void {
    const invMW = _invMatrix; // already set to inverse of group.mw
    const invProj = _projScreenMatrix.clone().invert();

    _localFrustumBox.makeEmpty();

    for (let x = -1; x <= 1; x += 2) {
      for (let y = -1; y <= 1; y += 2) {
        for (let z = -1; z <= 1; z += 2) {
          _va.set(x, y, z).applyMatrix4(invProj).applyMatrix4(invMW);
          _localFrustumBox.expandByPoint(_va);
        }
      }
    }
  }

  private static _matrixKey(m: Matrix4): string {
    const e = m.elements;
    return `${e[12].toFixed(1)},${e[13].toFixed(1)},${e[14].toFixed(1)}`;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 16 > 15 allowed; refactor later
  private _getOrCacheGeometry(
    geometry: BufferGeometry,
    mesh?: Mesh
  ): CachedGeometry | null {
    const key = geometry.uuid;
    let cached = this._geometryCache.get(key);
    if (cached) {
      if (mesh) {
        cached.matrixWorld.copy(mesh.matrixWorld);
        cached.matrixKey = CrossSectionContourComputer._matrixKey(
          mesh.matrixWorld
        );
      }
      return cached;
    }

    if (this._geometryCacheWeak.has(geometry)) return null;

    const posAttr = geometry.getAttribute('position') as
      | BufferAttribute
      | undefined;
    if (!posAttr?.array || posAttr.count === 0) {
      this._geometryCacheWeak.add(geometry);
      return null;
    }

    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    if (!bb || bb.isEmpty()) return null;

    const positions = new Float32Array(posAttr.array as Float32Array);

    const indexAttr = geometry.index;
    let index: IndexArray | null = null;
    let indexCount = 0;
    if (indexAttr?.array && indexAttr.count > 0) {
      index =
        indexAttr.array instanceof Uint32Array
          ? new Uint32Array(indexAttr.array)
          : new Uint16Array(indexAttr.array as Uint16Array);
      indexCount = indexAttr.count;
    }

    const mat = mesh
      ? Array.isArray(mesh.material)
        ? mesh.material[0]
        : mesh.material
      : null;
    const isBimMesh = !!(mat as { isWVMeshMaterial?: boolean } | null)
      ?.isWVMeshMaterial;

    cached = {
      positions,
      index,
      indexCount,
      bbMin: [bb.min.x, bb.min.y, bb.min.z],
      bbMax: [bb.max.x, bb.max.y, bb.max.z],
      matrixWorld: mesh ? mesh.matrixWorld.clone() : new Matrix4(),
      matrixKey: mesh
        ? CrossSectionContourComputer._matrixKey(mesh.matrixWorld)
        : '',
      isBimMesh,
    };
    this._geometryCache.set(key, cached);
    return cached;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 33 > 15 allowed; refactor later
  private _intersectRange(
    positions: Float32Array,
    index: IndexArray | null,
    start: number,
    end: number,
    frustumCull: boolean = false
  ): void {
    const nx = _localPlane.normal.x;
    const ny = _localPlane.normal.y;
    const nz = _localPlane.normal.z;
    const pw = _localPlane.constant;

    const fMinX = _localFrustumBox.min.x,
      fMinY = _localFrustumBox.min.y,
      fMinZ = _localFrustumBox.min.z;
    const fMaxX = _localFrustumBox.max.x,
      fMaxY = _localFrustumBox.max.y,
      fMaxZ = _localFrustumBox.max.z;

    for (let i = start; i < end; i += 3) {
      let ia: number, ib: number, ic: number;
      if (index) {
        ia = index[i];
        ib = index[i + 1];
        ic = index[i + 2];
      } else {
        ia = i;
        ib = i + 1;
        ic = i + 2;
      }

      const ax = positions[ia * 3],
        ay = positions[ia * 3 + 1],
        az = positions[ia * 3 + 2];
      const bx = positions[ib * 3],
        by = positions[ib * 3 + 1],
        bz = positions[ib * 3 + 2];
      const cx = positions[ic * 3],
        cy = positions[ic * 3 + 1],
        cz = positions[ic * 3 + 2];

      if (frustumCull) {
        const triMinX = Math.min(ax, bx, cx),
          triMaxX = Math.max(ax, bx, cx);
        const triMinY = Math.min(ay, by, cy),
          triMaxY = Math.max(ay, by, cy);
        const triMinZ = Math.min(az, bz, cz),
          triMaxZ = Math.max(az, bz, cz);
        if (
          triMaxX < fMinX ||
          triMinX > fMaxX ||
          triMaxY < fMinY ||
          triMinY > fMaxY ||
          triMaxZ < fMinZ ||
          triMinZ > fMaxZ
        )
          continue;
      }

      const da = ax * nx + ay * ny + az * nz + pw;
      const db = bx * nx + by * ny + bz * nz + pw;
      const dc = cx * nx + cy * ny + cz * nz + pw;

      const eps = 0.05;
      if (Math.abs(da) < eps && Math.abs(db) < eps && Math.abs(dc) < eps) {
        this._pushSegment(ax, ay, az, bx, by, bz);
        this._pushSegment(bx, by, bz, cx, cy, cz);
        this._pushSegment(cx, cy, cz, ax, ay, az);
        continue;
      }

      let hitCount = 0;
      let hx0 = 0,
        hy0 = 0,
        hz0 = 0;
      let hx1 = 0,
        hy1 = 0,
        hz1 = 0;

      if (da > 0 !== db > 0) {
        const t = da / (da - db);
        if (hitCount === 0) {
          hx0 = ax + t * (bx - ax);
          hy0 = ay + t * (by - ay);
          hz0 = az + t * (bz - az);
        } else {
          hx1 = ax + t * (bx - ax);
          hy1 = ay + t * (by - ay);
          hz1 = az + t * (bz - az);
        }
        hitCount++;
      }
      if (da > 0 !== dc > 0) {
        const t = da / (da - dc);
        if (hitCount === 0) {
          hx0 = ax + t * (cx - ax);
          hy0 = ay + t * (cy - ay);
          hz0 = az + t * (cz - az);
        } else {
          hx1 = ax + t * (cx - ax);
          hy1 = ay + t * (cy - ay);
          hz1 = az + t * (cz - az);
        }
        hitCount++;
      }
      if (hitCount < 2 && db > 0 !== dc > 0) {
        const t = db / (db - dc);
        if (hitCount === 0) {
          hx0 = bx + t * (cx - bx);
          hy0 = by + t * (cy - by);
          hz0 = bz + t * (cz - bz);
        } else {
          hx1 = bx + t * (cx - bx);
          hy1 = by + t * (cy - by);
          hz1 = bz + t * (cz - bz);
        }
        hitCount++;
      }

      if (hitCount === 2) {
        this._pushSegment(hx0, hy0, hz0, hx1, hy1, hz1);
      }
    }
  }

  private _pushSegment(
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number
  ): void {
    const offset = this._segmentCount * 6;
    if (offset + 6 > this._segmentBuffer.length) {
      const newBuf = new Float32Array(this._segmentBuffer.length * 2);
      newBuf.set(this._segmentBuffer);
      this._segmentBuffer = newBuf;
    }
    this._segmentBuffer[offset] = x0;
    this._segmentBuffer[offset + 1] = y0;
    this._segmentBuffer[offset + 2] = z0;
    this._segmentBuffer[offset + 3] = x1;
    this._segmentBuffer[offset + 4] = y1;
    this._segmentBuffer[offset + 5] = z1;
    this._segmentCount++;
  }
}
